/**
 * ElecBid: the job-note card in the Jai-Man chat.
 *
 * A tech told Jai-Man what they did on a job; Jai-Man drafted the Jobber note
 * (api/_jobNoteDraft.js) and the draft arrives here. Nothing is in Jobber yet.
 * This card is the one place it can be posted from: it shows WHICH job (number,
 * client, address) and the exact note, and "Add to job" sends the signed draft
 * to /api/crew-schedule. Jai-Man itself has no way to post. The tap is the
 * confirmation, on purpose, because a stock note on the wrong job books a cost
 * there that deleting the note does not take back.
 *
 * WHAT HAPPENED TO A DRAFT OUTLIVES THE CARD. The chat panel unmounts its
 * messages whenever it is closed or History is opened, and a card that came
 * back saying "not added yet" for a note that IS on the job would have the tech
 * add it again: a second note, and the stock sweep books every note. So
 * everything the card knows is kept in OUTCOMES (per draft id, for the life of
 * the page), including a post that is still on its way. The card on screen
 * only shows what OUTCOMES says, so a card that is reopened while its note is
 * being added shows "Adding…" and then the real answer. Each change is also
 * reported to the widget by draft id (onOutcome), which tells Jai-Man on the
 * next turn.
 *
 * After posting it can also say how the stock on the note was priced, which the
 * stock sweep otherwise tells nobody.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { apiFetch } from "../lib/apiFetch";

const C = {
  bg: "#0d0f12", card: "#161a20", border: "#2a3140", lime: "#a5ce3a",
  text: "#e8eaf0", muted: "#8892a4", faint: "#5a6374", red: "#e5484d", yellow: "#f5d90a",
};

// draftId → { state, result, pricing, error, attempted }. See the header.
// state: idle | posting (Add is on its way) | checking (the old-draft button is
// asking) | unsure (Add went out and no answer came back: the note may be on
// the job) | posted | queued | failed | cancelled | expired | refused (the
// server would not add it: a later note is on the job)
// attempted: Add was tapped on this card and the note did not go on the job.
const OUTCOMES = new Map();
const WATCHERS = new Map(); // draftId → the cards on screen showing that draft
const NOTHING = Object.freeze({});
const SETTLED = new Set(["posted", "queued", "failed", "cancelled", "expired", "refused"]);

// A queued note is sent by a job that runs every ten minutes, and Jobber's
// final "no" comes after eight tries, about eighty minutes on. So a queued card
// looks by itself at about 1, 6, 11, 21, 41 and 85 minutes: these are the waits
// between one look and the next.
const QUEUED_LOOK_GAPS_MS = [1, 5, 5, 10, 20, 44].map((m) => m * 60e3);

// Add went out and no answer came back. The note may be on the job, so the
// card asks (a look never adds anything) at about 3, 10 and 25 seconds. A "no
// note" answer only counts on the last look: the first request can still be on
// its way through the server before that.
const UNSURE_LOOK_GAPS_MS = [3e3, 7e3, 15e3];

const outcomeOf = (id) => OUTCOMES.get(id) || NOTHING;

function watchOutcome(id, onChange) {
  if (!WATCHERS.has(id)) WATCHERS.set(id, new Set());
  WATCHERS.get(id).add(onChange);
  return () => {
    const set = WATCHERS.get(id);
    if (!set) return;
    set.delete(onChange);
    if (!set.size) WATCHERS.delete(id);
  };
}

// A note that is on the job (posted), or saved to be sent (queued), stays that
// way. A late "cancelled", "too old" or "not added yet" must never cover it up:
// the tech would add it again, and Jai-Man would be told nothing was added. A
// queued note can only move on to posted or failed. And a tap on Add that is
// still on its way cannot be cancelled.
function mayFollow(from, to) {
  if (from === "posted") return to === "posted";
  if (from === "queued") return to === "queued" || to === "posted" || to === "failed";
  if (from === "posting") return to !== "cancelled";
  // Nobody knows yet whether it is on the job. Only an answer from the server
  // ends that: it is there (posted, queued, failed), or it is not (idle).
  if (from === "unsure") return to === "unsure" || to === "posted" || to === "queued" || to === "failed" || to === "idle";
  return true;
}

// The one way OUTCOMES changes. Returns false when the change was not allowed.
function recordOutcome(id, patch) {
  const was = outcomeOf(id);
  if (patch.state && !mayFollow(was.state || "idle", patch.state)) return false;
  OUTCOMES.set(id, { ...was, ...patch });
  for (const onChange of [...(WATCHERS.get(id) || [])]) onChange();
  return true;
}

const btn = (kind) => ({
  border: `1px solid ${kind === "primary" ? C.lime : C.border}`,
  background: kind === "primary" ? C.lime : "transparent",
  color: kind === "primary" ? "#0d0f12" : C.text,
  borderRadius: 10, padding: "11px 14px", minHeight: 42, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
});

// A refusal is an answer: the server read the request and said no (ok: false).
// Everything else that is not a clean yes is NO ANSWER: the request was lost,
// timed out or crashed part-way, and it may have been carried out all the same.
// The two must not be mixed up. "Nothing was added" after a lost answer is how
// a note that IS on the job gets added a second time.
async function call(payload) {
  let res;
  let data = null;
  try {
    res = await apiFetch("/api/crew-schedule", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
    });
    data = await res.json().catch(() => null);
  } catch {
    const err = new Error("No answer came back.");
    err.noAnswer = true;
    throw err;
  }
  if (res.ok && data && data.ok !== false) return data;
  const err = new Error((data && data.error) || "That did not go through. Try again.");
  err.noAnswer = !data || res.status >= 500;
  err.expired = !!data?.expired;
  // Someone added a note to this job after this card was written.
  err.superseded = !!data?.superseded;
  throw err;
}

// Where a post landed, from the server's answer.
const stateOf = (data) => (data.posted ? "posted" : data.failed || data.queued === false ? "failed" : "queued");

// One section of the pricing read-back, in plain words.
function PricedSection({ s }) {
  const head = {
    priced: "priced",
    waiting: "not priced yet",
    pricing: "being priced now",
    held: "held for the office to check",
    skipped: "nothing could be priced",
    failed: "pricing failed, it will be tried again",
  }[s.state] || s.state;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: s.state === "priced" ? C.lime : s.state === "waiting" || s.state === "pricing" ? C.muted : C.yellow }}>
        {s.label}: {head}{typeof s.total === "number" ? ` ($${Math.abs(s.total).toFixed(2)})` : ""}
      </div>
      {s.lines.map((l, i) => (
        // An estimate is stored as matched too, so test it first.
        <div key={i} style={{ fontSize: 13, color: C.text, marginTop: 3, lineHeight: 1.35 }}>
          {l.estimated ? "≈" : l.matched ? "✓" : "•"} {l.raw}
          {l.estimated ? <span style={{ color: C.yellow }}> (estimated, not in the price book)</span> : null}
          {!l.estimated && l.matched && l.part ? <span style={{ color: C.muted }}> → {l.part}</span> : null}
          {!l.estimated && !l.matched ? <span style={{ color: C.yellow }}> (needs a price)</span> : null}
        </div>
      ))}
      {s.reason && s.state !== "priced" && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{s.reason}</div>}
    </div>
  );
}

/**
 * draft:      from the chat response (noteDrafts)
 * readOnly:   the owner is viewing as someone else, so nothing can be added
 * superseded: a later reply drafted this job again, or says it replaces this
 *             card's job number (the person said the number was wrong)
 * busy:       Jai-Man is writing a reply right now. It may be a correction to
 *             this card, so Add waits for it.
 * onOutcome:  (draftId, { state, jobNumber }) on every change: being added,
 *             added, queued, failed, cancelled, not added
 */
export default function JobNoteCard({ draft, readOnly = false, superseded = false, busy = false, onOutcome }) {
  const id = draft?.draftId || "";
  // The card shows what OUTCOMES holds for this draft, and follows it when it
  // changes. An answer that lands after the chat was closed and reopened was
  // asked for by a copy of this card that is gone; this copy still shows it.
  const watch = useCallback((onChange) => watchOutcome(id, onChange), [id]);
  const kept = useSyncExternalStore(watch, () => outcomeOf(id));
  const state = kept.state || "idle";
  const result = kept.result || null;
  const pricing = kept.pricing || null;
  const error = kept.error || "";
  const [checking, setChecking] = useState(false);
  const [looking, setLooking] = useState(false);

  // A queued note sends itself, or Jobber refuses it for good, and nothing else
  // tells the tech which. So the card looks by itself while it is on screen:
  // straight away when the chat is reopened on a card that is already queued,
  // then on the waits in QUEUED_LOOK_GAPS_MS, until the note is on the job or
  // refused. "Check again" is there the whole time. One timer at a time, and
  // it is cleared when the card leaves the screen.
  const outboxId = result?.outboxId || null;
  const queuedAtMount = useRef(state === "queued");
  const selfCheck = useRef(null);
  const [looks, setLooks] = useState(0);
  useEffect(() => {
    if (readOnly || state !== "queued" || !outboxId) return undefined;
    const first = looks === 0 && queuedAtMount.current;
    const wait = first ? 0 : QUEUED_LOOK_GAPS_MS[queuedAtMount.current ? looks - 1 : looks];
    if (wait === undefined) return undefined; // looked for long enough; the button is still there
    const timer = setTimeout(async () => {
      if (selfCheck.current) await selfCheck.current();
      setLooks((n) => n + 1);
    }, wait);
    return () => clearTimeout(timer);
  }, [readOnly, state, outboxId, looks]);

  // Add went out and no answer came back. Ask whether a note was made from this
  // card, a few times, by itself. Same rules: one timer, cleared on the way out.
  const unsureLook = useRef(null);
  const [asked, setAsked] = useState(0);
  useEffect(() => {
    if (readOnly || state !== "unsure") return undefined;
    const wait = UNSURE_LOOK_GAPS_MS[asked];
    if (wait === undefined) return undefined; // "Check again" takes over
    const timer = setTimeout(async () => {
      if (unsureLook.current) await unsureLook.current(asked >= UNSURE_LOOK_GAPS_MS.length - 1);
      setAsked((n) => n + 1);
    }, wait);
    return () => clearTimeout(timer);
  }, [readOnly, state, asked]);

  if (!draft?.token || !draft?.job) return null;
  const job = draft.job;

  // Every change goes through OUTCOMES, so each copy of this card shows the
  // same thing, and through onOutcome, so the widget can tell Jai-Man.
  const set = (patch) => {
    if (!recordOutcome(id, patch)) return false;
    if (patch.state && typeof onOutcome === "function") onOutcome(id, { state: patch.state, jobNumber: job.number, attempted: !!outcomeOf(id).attempted });
    return true;
  };

  const pastTime = draft.expiresAt ? Date.now() > new Date(draft.expiresAt).getTime() : false;
  const done = state === "posted" || state === "queued";
  const settled = SETTLED.has(state);
  const inFlight = state === "posting" || state === "checking";
  const unsure = state === "unsure";

  // The server looks for a note already made from this draft BEFORE it applies
  // the 30-minute limit, so tapping an old card can never make a second note:
  // it either finds the first one or says the draft is too old.
  // checkOnly is the old-draft button. It is this device's clock that says a
  // card is old, and that clock can be wrong, so that button must never add
  // anything: the server only answers whether a note was made from this draft.
  const send = async (checkOnly) => {
    const now = outcomeOf(id).state;
    if (now === "posting" || now === "checking" || now === "unsure" || SETTLED.has(now)) return; // already on its way, or already decided
    if (busy && !checkOnly) return;
    if (!set({ state: checkOnly ? "checking" : "posting", error: "" })) return;
    try {
      const data = await call({ action: "post-jaiman-note", token: draft.token, noteText: draft.noteText, ...(checkOnly ? { checkOnly: true } : {}) });
      // Asked only to check, and no note was ever made from this draft.
      if (data.notAdded) set({ state: "expired" });
      else set({ state: stateOf(data), result: data });
    } catch (e) {
      // No answer to an Add: the note may be on the job. Do not say it is not.
      if (e.noAnswer && !checkOnly) { setAsked(0); set({ state: "unsure", error: "" }); return; }
      if (e.noAnswer) { set({ state: "idle", error: "Could not check just now. Try again when you have signal." }); return; }
      // superseded: a note went on this job after this card was written. The
      // server's sentence says who added it and what to do; nothing was added.
      // Any other refusal leaves the card as it was, with the reason, and
      // remembers that an Add was tried and did not go through.
      set({ state: e.expired ? "expired" : e.superseded ? "refused" : "idle", error: e.message, ...(checkOnly ? {} : { attempted: true }) });
    }
  };

  // After a lost answer: was a note made from this card? A look never adds.
  // `final` = this answer may be taken as "no": enough time has gone by for the
  // first request to have finished one way or the other.
  const lookAfterLostAnswer = async (final) => {
    if (outcomeOf(id).state !== "unsure") return;
    try {
      const data = await call({ action: "post-jaiman-note", token: draft.token, noteText: draft.noteText, checkOnly: true });
      if (!data.notAdded) set({ state: stateOf(data), result: data, error: "" });
      else if (final) set({ state: "idle", attempted: true, error: "That did not reach ElecBid, so nothing was added. Tap Add to try again." });
    } catch (e) {
      // A refusal here is an answer about the card itself (signed out, say).
      if (!e.noAnswer) set({ state: "idle", attempted: true, error: e.message });
    }
  };
  unsureLook.current = lookAfterLostAnswer;
  const lookNow = async () => { setLooking(true); await lookAfterLostAnswer(true); setLooking(false); };

  // quiet = the card's own look at a queued note: no spinner, no error text.
  const check = async ({ quiet = false } = {}) => {
    const askFor = outcomeOf(id).result?.outboxId;
    if (!askFor) return;
    if (!quiet) { setChecking(true); set({ error: "" }); }
    try {
      const data = await call({ action: "jaiman-note-status", outboxId: askFor });
      const was = outcomeOf(id);
      set({ pricing: data, error: "" });
      if (data.posted && was.state !== "posted") set({ state: "posted", result: { ...was.result, credited: !!data.credited, hasStock: !!data.hasStock, error: null } });
      else if (data.failed && was.state !== "failed") set({ state: "failed", result: { ...was.result, error: data.error || was.result?.error || null } });
    } catch (e) { if (!quiet) set({ error: e.message }); }
    if (!quiet) setChecking(false);
  };
  selfCheck.current = () => check({ quiet: true });

  const replaced = superseded && !settled && !inFlight && !unsure;
  // Add was tapped on this card, it did not go through, and a newer card has
  // since taken its place. If that newer card was written while this one was
  // being added it holds only what was said after it, so "use the newer note"
  // would lose this one. Say what is known and let the tech check.
  const triedThenReplaced = replaced && !!kept.attempted;
  const heading = state === "posted" ? "Added to the job"
    : state === "queued" ? "Waiting on Jobber"
    : state === "failed" ? "Not added"
    : state === "cancelled" ? "Not added"
    : state === "expired" ? "Not added"
    : state === "refused" ? "Not added"
    : unsure ? "Checking whether it was added"
    : triedThenReplaced ? "Not added"
    : replaced ? "Replaced by the newer note below"
    : "Job note, not added yet";
  const showNote = state !== "cancelled" && (!replaced || triedThenReplaced);
  const canAct = !settled && !replaced && !inFlight && !unsure;

  return (
    <div style={{ marginTop: 8, border: `1px solid ${canAct || inFlight ? C.lime : unsure ? C.yellow : C.border}`, borderRadius: 12, background: C.card, padding: 12, maxWidth: 520, opacity: replaced ? 0.7 : 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: C.muted }}>{heading}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: "#fff", marginTop: 4, lineHeight: 1.25 }}>
        Job #{job.number}{job.client ? ` · ${job.client}` : ""}
      </div>
      {(job.title || job.address) && (
        <div style={{ fontSize: 13, color: "#b8c0cf", marginTop: 3, lineHeight: 1.4 }}>
          {[job.title, job.address].filter(Boolean).join(" · ")}
        </div>
      )}

      {canAct && (draft.warnings || []).map((w, i) => (
        <div key={i} style={{ fontSize: 13, color: C.yellow, marginTop: 8, lineHeight: 1.4 }}>⚠ {w}</div>
      ))}

      {showNote && (
        <pre style={{ margin: "10px 0 0", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", fontFamily: "ui-monospace, Consolas, monospace", fontSize: 13.5, lineHeight: 1.45, color: C.text, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: done ? 140 : 320, overflowY: "auto" }}>
          {draft.noteText}
        </pre>
      )}

      {replaced && !triedThenReplaced && <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>Nothing was added from this one. Use the newer note below.</div>}
      {triedThenReplaced && (
        <div style={{ fontSize: 13, color: C.yellow, marginTop: 8, lineHeight: 1.4 }}>
          This note was NOT added. Check that the newer note below has all of it. If it only has part, ask Jai-Man to write the whole note again.
        </div>
      )}

      {error && !replaced && <div style={{ fontSize: 13, color: C.red, marginTop: 8, lineHeight: 1.4 }}>{error}</div>}

      {unsure && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, color: C.yellow, lineHeight: 1.4 }}>
            No answer came back, so this note may already be on the job. {asked < UNSURE_LOOK_GAPS_MS.length ? "Checking now." : "ElecBid could not find out yet."} Do not ask Jai-Man to write it again until this card says whether it was added.
          </div>
          {!readOnly && asked >= UNSURE_LOOK_GAPS_MS.length && (
            <button type="button" style={{ ...btn(), marginTop: 8, opacity: looking ? 0.6 : 1 }} onClick={lookNow} disabled={looking}>
              {looking ? "Checking…" : "Check again"}
            </button>
          )}
        </div>
      )}

      {(canAct || inFlight) && (
        readOnly ? (
          <div style={{ fontSize: 13, color: C.muted, marginTop: 10 }}>View-only: notes cannot be added while viewing as someone else.</div>
        ) : pastTime ? (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 13, color: C.yellow, lineHeight: 1.4 }}>This draft is more than 30 minutes old.</div>
            <button type="button" style={{ ...btn(), marginTop: 8, opacity: inFlight ? 0.6 : 1 }} onClick={() => send(true)} disabled={inFlight}>
              {inFlight ? "Checking…" : "Check whether it was added"}
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            <button type="button" style={{ ...btn("primary"), opacity: inFlight || busy ? 0.6 : 1 }} onClick={() => send(false)} disabled={inFlight || busy}>
              {state === "posting" ? "Adding…" : `Add to job #${job.number}`}
            </button>
            <button type="button" style={btn()} onClick={() => set({ state: "cancelled", error: "" })} disabled={inFlight}>Cancel</button>
          </div>
        )
      )}
      {canAct && !readOnly && !pastTime && (
        busy
          ? <div style={{ fontSize: 12, color: C.yellow, marginTop: 8, lineHeight: 1.4 }}>Jai-Man is answering. Wait for the reply before you add this.</div>
          : <div style={{ fontSize: 12, color: C.faint, marginTop: 8, lineHeight: 1.4 }}>Check the job is right. To change the note, tell Jai-Man what to fix.</div>
      )}

      {state === "cancelled" && <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>Nothing was added to Jobber.</div>}
      {state === "expired" && <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>It was not added. Ask Jai-Man to write it again.</div>}

      {state === "failed" && (
        <div style={{ fontSize: 13, color: C.red, marginTop: 10, lineHeight: 1.4 }}>
          This did not go through{result?.error ? ` (${result.error})` : ""}. Nothing was added. Ask Jai-Man to write it again, or tell the office.
        </div>
      )}

      {state === "queued" && (
        <div style={{ fontSize: 13, color: C.yellow, marginTop: 10, lineHeight: 1.4 }}>
          Jobber did not take it yet{result?.error ? ` (${result.error})` : ""}. It is saved and will be sent again by itself. Do not add it a second time.
        </div>
      )}

      {state === "posted" && (
        <div style={{ fontSize: 13, color: C.text, marginTop: 10, lineHeight: 1.45 }}>
          <span style={{ color: C.lime, fontWeight: 700 }}>✓ It is on job #{job.number} in Jobber.</span>
          {result?.hasStock ? " The stock gets priced and added to the job within about 15 minutes." : ""}
          {result?.credited ? " It counts as your note for today." : ""}
        </div>
      )}

      {done && (result?.hasStock || state === "queued") && result?.outboxId && (
        <div style={{ marginTop: 10 }}>
          <button type="button" style={{ ...btn(), fontSize: 13 }} onClick={() => check()} disabled={checking}>
            {checking ? "Checking…" : state === "queued" ? "Check again" : "Check the pricing"}
          </button>
        </div>
      )}

      {pricing && pricing.posted && pricing.hasStock && (
        <div style={{ marginTop: 6 }}>
          {pricing.sections.map((s) => <PricedSection key={s.kind} s={s} />)}
          {pricing.sections.some((s) => s.state === "waiting") && (pricing.minutesSincePosted ?? 0) > 40 && (
            <div style={{ fontSize: 12, color: C.yellow, marginTop: 8, lineHeight: 1.4 }}>
              Not priced after {pricing.minutesSincePosted} minutes. Tell the office: job #{pricing.jobNumber}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
